import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { BottomSheetContentLayout, Box, Button, Column, FullBox, Row, Spinner, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { getContent } from '~/components/ActivityLogItem/ActivityLogItem.utils';
import { useStore, useStoreActions } from '~/contexts';
import { useActivityLog } from '~/hooks/reactQuery/queries/useActivityLog';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel, timeModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import styles from './ActivityLogBottomSheetContent.module.scss';
export const GetActivityLogBottomSheetContentTID = (parentTestId)=>getTestIds(parentTestId);
export function ActivityLogBottomSheetContent(param) {
    let { agreementId, testId } = param;
    const { ActivityLogBottomSheetContentTestIds } = useMemo(()=>GetActivityLogBottomSheetContentTID(testId), [
        testId
    ]);
    const { api, logBI } = useStoreActions();
    const isVendor = useStore(selectors.user.isVendor);
    const featureFlags = useFeatureFlags();
    const { t } = useLocaleKeys();
    const sheetTexts = t.components.activityLogBottomSheet;
    const relationship = useRestfulWrapper({
        fetch: ()=>api.relationship.get(agreementId)
    });
    const { activityLog: activityLogData, isLoading: activityLogLoading, error: activityLogError, refresh: activityLogRefresh } = useActivityLog(agreementId, {
        surface: 'bottomSheet',
        refreshOnHookMount: true
    });
    const retry = ()=>{
        relationship.refresh();
        activityLogRefresh().catch(()=>{});
    };
    const content = relationship.error || activityLogError ? /*#__PURE__*/ _jsxs(FullBox, {
        testId: ActivityLogBottomSheetContentTestIds.Error(),
        className: styles.errorContainer,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                children: sheetTexts.couldntLoad()
            }),
            /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'retry_load_activity_log'
                },
                testId: ActivityLogBottomSheetContentTestIds.RetryButton(),
                onClick: retry,
                children: sheetTexts.tryAgain()
            })
        ]
    }) : !relationship.data || relationship.loading || activityLogLoading ? /*#__PURE__*/ _jsx(FullBox, {
        testId: ActivityLogBottomSheetContentTestIds.Spinner(),
        className: styles.spinnerContainer,
        children: /*#__PURE__*/ _jsx(Spinner, {
            centeredInBox: true,
            centerBoxHeight: 300
        })
    }) : /*#__PURE__*/ _jsx(Box, {
        testId: ActivityLogBottomSheetContentTestIds.ActivityList(),
        className: styles.activityList,
        children: activityLogData.map((entry, idx)=>{
            var _relationshipModel_sellerFullName, _relationshipModel_buyerFullName;
            const entryTestId = "activity-log-".concat(entry.id());
            const content = getContent({
                entry,
                vendorName: (_relationshipModel_sellerFullName = relationshipModel.sellerFullName(relationship.data)) !== null && _relationshipModel_sellerFullName !== void 0 ? _relationshipModel_sellerFullName : '',
                clientName: (_relationshipModel_buyerFullName = relationshipModel.buyerFullName(relationship.data)) !== null && _relationshipModel_buyerFullName !== void 0 ? _relationshipModel_buyerFullName : '',
                isVendor,
                relationship: relationship.data,
                testId: entryTestId,
                logBI,
                featureFlags,
                t
            });
            if (!content) return null;
            return /*#__PURE__*/ _jsxs(Row, {
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsxs(Column, {
                        alignItems: "center",
                        gap: 8,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: cn(styles.line, {
                                    [styles.transparent]: idx === 0
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.dot
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: cn(styles.line, {
                                    [styles.transparent]: idx === activityLogData.length - 1
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Column, {
                        className: styles.entryContent,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                testId: entryTestId,
                                children: content
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                skin: "secondary",
                                children: timeModel.formatDateWithTime(entry.timestamp())
                            })
                        ]
                    })
                ]
            }, entry.id());
        })
    });
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        header: {
            title: relationship.data ? relationshipModel.opposingPartyName(relationship.data, isVendor) : undefined,
            subtitle: relationship.data ? relationshipModel.displayedTitle(relationship.data, isVendor) : undefined
        },
        children: /*#__PURE__*/ _jsx(FullBox, {
            testId: ActivityLogBottomSheetContentTestIds.Root(),
            children: content
        })
    });
}
